import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSignMessage } from "wagmi";
import i18next from "i18next";
import { ApiFortAgentPayMixedFusd } from "@/api";
import {
  FortAgentPayMixedFusdResultCode,
  type FortAgentPayMixedFusdRequest,
} from "@/api/types";
import { formatFortAgentPayMixedFusdMessage } from "@/lib/fortAgentPayMixedFusdSign";
import { parseChainError } from "@/lib/chainError";
import { showError, showSuccess } from "@/lib/toast";
import { getTokenName } from "@/config/common";
import { CCC_FUSD_ADDRESS, USDT_ADDRESS } from "@/config/env";
import { useUserStore } from "@/store/useUserStore";

export function useFortAgentPayMixedFusd() {
  const queryClient = useQueryClient();
  const { signMessageAsync } = useSignMessage();
  const authType = useUserStore(state => state.authType);
  const isEmailAuth = authType === "email";

  return useMutation({
    mutationFn: async (
      params: Pick<FortAgentPayMixedFusdRequest, "agentId">
    ) => {
      const { agentId } = params;
      if (isEmailAuth) {
        const res = await ApiFortAgentPayMixedFusd({ agentId });
        return res.data;
      }

      const payTick = Date.now();
      const message = formatFortAgentPayMixedFusdMessage(agentId, payTick);
      const signature = await signMessageAsync({ message });
      const res = await ApiFortAgentPayMixedFusd({
        agentId,
        payTick,
        signature,
      });
      return res.data;
    },
    onSuccess: data => {
      const code = data.resultCode;
      if (code === FortAgentPayMixedFusdResultCode.Success) {
        queryClient.invalidateQueries({ queryKey: ["myFortAgents"] });
        showSuccess(
          i18next.t("toast.fortAgent.payMixedFusd.success", {
            tokenName: getTokenName(CCC_FUSD_ADDRESS),
            tokenNameUsdt: getTokenName(USDT_ADDRESS),
          })
        );
        return;
      }
      const key =
        FORT_AGENT_PAY_MIXED_FUSD_RESULT_I18N_KEY[
          code as keyof typeof FORT_AGENT_PAY_MIXED_FUSD_RESULT_I18N_KEY
        ] ?? "toast.fortAgent.payMixedFusd.result.unknown";
      showError(
        i18next.t(key, {
          tokenName: getTokenName(CCC_FUSD_ADDRESS),
          tokenNameUsdt: getTokenName(USDT_ADDRESS),
        })
      );
    },
    onError: (err: unknown) => {
      showError(
        i18next.t(parseChainError(err), {
          tokenName: getTokenName(CCC_FUSD_ADDRESS),
          tokenNameUsdt: getTokenName(USDT_ADDRESS),
        })
      );
    },
  });
}

/** 与 {@link FortAgentPayMixedFusdResultCode} 一一对应（`toast.fortAgent.payMixedFusd.result.*`） */
export const FORT_AGENT_PAY_MIXED_FUSD_RESULT_I18N_KEY: Record<
  Exclude<
    FortAgentPayMixedFusdResultCode,
    FortAgentPayMixedFusdResultCode.Success
  >,
  string
> = {
  [FortAgentPayMixedFusdResultCode.WalletNotBound]:
    "toast.fortAgent.payMixedFusd.result.walletNotBound",
  [FortAgentPayMixedFusdResultCode.InvalidAgentId]:
    "toast.fortAgent.payMixedFusd.result.invalidAgentId",
  [FortAgentPayMixedFusdResultCode.AgentNotFoundOrOwned]:
    "toast.fortAgent.payMixedFusd.result.agentNotFoundOrOwned",
  [FortAgentPayMixedFusdResultCode.NotMixedMode]:
    "toast.fortAgent.payMixedFusd.result.notMixedMode",
  [FortAgentPayMixedFusdResultCode.AlreadyPaid]:
    "toast.fortAgent.payMixedFusd.result.alreadyPaid",
  [FortAgentPayMixedFusdResultCode.NoFusdOwed]:
    "toast.fortAgent.payMixedFusd.result.noFusdOwed",
  [FortAgentPayMixedFusdResultCode.ParseSignErr]:
    "toast.fortAgent.payMixedFusd.result.parseSignErr",
  [FortAgentPayMixedFusdResultCode.SignerErr]:
    "toast.fortAgent.payMixedFusd.result.signerErr",
  [FortAgentPayMixedFusdResultCode.ExpireSignErr]:
    "toast.fortAgent.payMixedFusd.result.expireSignErr",
  [FortAgentPayMixedFusdResultCode.InsufficientGameBalance]:
    "toast.fortAgent.purchase.result.insufficientMixedFusdGameNow",
  [FortAgentPayMixedFusdResultCode.Busy]:
    "toast.fortAgent.payMixedFusd.result.busy",
  [FortAgentPayMixedFusdResultCode.InsufficientWaitClaimFusd]:
    "toast.fortAgent.purchase.result.insufficientMixedFusdWaitNow",
  [FortAgentPayMixedFusdResultCode.DeductConflict]:
    "toast.fortAgent.payMixedFusd.result.deductConflict",
};
